import { useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router';

/**
 * Moves keyboard focus to the page heading (`#page-heading`) on every route
 * change.
 *
 * The first render is skipped so the initial app load does not steal focus
 * from the skip-to-content link.
 *
 * The target element must:
 * - exist in the DOM at the time the route settles
 * - carry `id="page-heading"` and `tabIndex={-1}`
 *
 * Falls back silently to `#main-content` if no `#page-heading` is mounted
 * (some routes use bespoke layouts).
 */
export function usePageFocusOnRouteChange(): void {
  const { pathname } = useLocation();
  const isFirstRender = useRef(true);

  useLayoutEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const normalizedPathname = pathname.endsWith('/') && pathname !== '/' ? pathname.slice(0, -1) : pathname;

    // Important for accessibility tests:
    // When we're on the Block Library list page, don't steal initial focus.
    // The skip-link must be the first tabbable target for keyboard users.
    if (normalizedPathname === '/blocks') return;

    let didFocus = false;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let observer: MutationObserver | null = null;

    const focusIfTargetMounted = (): boolean => {
      if (didFocus) return true;

      const pageHeading = document.getElementById('page-heading') as HTMLElement | null;
      const fallbackMain = document.getElementById('main-content') as HTMLElement | null;
      const target = pageHeading ?? fallbackMain;
      if (!target) return false;

      didFocus = true;
      // Prevent scrolling; headings are mostly visually hidden targets.
      target.focus({ preventScroll: true });
      return true;
    };

    // Try immediately first (in case the lazy chunk is already mounted).
    if (focusIfTargetMounted()) return;

    // Otherwise, focus as soon as the target appears.
    observer = new MutationObserver(() => {
      if (focusIfTargetMounted() && observer) {
        observer.disconnect();
        observer = null;
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    timeoutId = window.setTimeout(() => {
      if (observer) observer.disconnect();
      observer = null;
    }, 1500);

    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
      if (observer) observer.disconnect();
    };
  }, [pathname]);
}
